/**
 * The error details returned by the External API.
 *
 * @typedef {Object} PrintixErrorResponse
 * @property {String} errorCode Short error code that can sometimes be looked up in a captions file.
 * @property {String} errorText An expanded version of {@link #getParameterizedErrorText}.
 * @property {String} exClass The exception class to instantiate on calling services.
 * @property {UUID} id The ID of the error.
 * @property {String} originatingService The service the error is originally from.
 * @property {String} parameterizedErrorText A longer text describing what went wrong. The text may include parameters that looks like {?} where ? is a positive integer.
 * @property {String[]} parameters A list of parameters of the {@link PrintixErrorResponse#parameterizedErrorText}.
 * @property {Number} statusCode The status code to use when serializing this to a response.
 * @property {String} errorCaptionOverride Custom property added by the Admin Portal. Indicates if the default caption should be replaced with something else.
 * @property {String} manualLink Custom property added by the Admin Portal. A URL for the related page of the Online Documentation.
 */

/**
 * Internal destination type names.
 * @typedef {('Connector' | 'SharePoint'| 'OneDriveForBusiness')} InternalDestinationName
 */

/**
 * The destination type.
 * @typedef {Object} DestinationType
 * @property {TenantIdentityProviderName} identityProviderName The name of Identity Provider whose directories are used by the given destination.
 * @property {InternalDestinationName} internalName The internal identifier of the destination type.
 * @property {(profile: DestinationProfileModel) => DestinationModel} createProfileModel Creates a new model to store info about destination profile fields.
 * @property {(directory: TenantIdentityProvidersResource_Directory) => String} getAdminConsentUrl The URL through which admin consent can be granted for the given identity provider.
 * @property {(directory: TenantIdentityProvidersResource_Directory) => Boolean} isAccessGranted Determines if admin consent was granted for the given Identity Provider directory.
 */

/**
 * @typedef {Object.<string, DestinationType>} DestinationModels
 */

/**
 * The scope.
 * @typedef {Object} ScopeType
 * @property {DestinationProfileModel} inDestinationProfile The destination profile to edit if the dialog is opened to modify an existing destination profile.
 * When the dialog is opened for destination profile creation, this value is null.
 * @property {DestinationProfileModel} destinationProfile The destination profile object edited on this dialog.
 * @property {DestinationModel} model The model that contains the current values and related business logic for all destination profile fields.
 * @property {DestinationType} selectedDestination The currently selected destination.
 * @property {TenantIdentityProvidersResource_Directory} selectedDirectory The currently selected Identity Provider directory.
 * @property {DestinationModels} availableDestinations The array of available destination types.
 * @property {TenantIdentityProvidersResource_Directory[]} availableDirectories The array of Identity Provider directories are available for the selected destination type.
 * @property {(fieldName : String) => void} onFieldLostFocus Event handler for the input field's onBlur event.
 * @property {(fieldName : String) => void} onFieldChanged Event handler for the input field's onChanged event.
 * @property {angular.IHttpResponse<PrintixErrorResponse>} error The error dialog's model.
 * @property {boolean} destinationProfileOutdated Indicates when someone else modified the destination profile in the meantime
 *  and our working copy of the destination profile become obsolete. When this happens, the profile will must be checked out
 *  again to create a new working copy of its most up-to-date state.
 */

/**
 * Destination profile model.
 * @typedef {Object} DestinationProfileModel
 * @property {DestinationProfileResource} info
 * @property {{isSelected: boolean}} options - Indicates if the destination profile is selected.
 */

/**
 * Destination profile resource handled by the External API.
 *
 * @typedef {Object} DestinationProfileResource
 * @property {String} name
 * @property {UUID} profileId
 * @property {InternalDestinationName} type
 * @property {'UNDER_CREATION'|'CREATED'} state
 *
 * @property {UUID} directory For SharePoint/OneDrive destination profiles only.
 * @property {String} site For SharePoint destination profiles only.
 * @property {String} siteName For SharePoint destination profiles only.
 * @property {String} documentLibrary For SharePoint destination profiles only.
 * @property {String} documentLibraryName For SharePoint destination profiles only.
 * @property {String} folder For SharePoint/OneDrive destination profiles only.
 * @property {String} folderName For SharePoint destination profiles only.
 * @property {String} profileUrl For SharePoint destination profiles only. The documentLibrary and folder properties concatenated.
 *
 * @property {String} connectorUrl For Connector destination profiles only.
 * @property {String} networkId For Connector destination profiles only.
 * @property {String} signatureAlgorithm For Connector destination profiles only.
 * @property {String[]} secretKeys For Connector destination profiles only.
 */


/**
 * Destination profile field model.
 * @typedef {Object} DestinationModel
 * @property {Object.<string,DestinationProfileField>} fields
 */

/**
 * @typedef {Object} DestinationProfileField
 * @property {string} error A validation error description if the current value is incorrect, otherwise null.
 * @property {string} value The field's current value.
 * @property {() => void} validate Validates the field's current value.
 */

/**
 * An Identity Provider reference
 * @typedef {('azure'|null)} TenantIdentityProviderName
 */

/**
 * @typedef {Object} TenantIdentityProvidersResource_OIDCConfig
 */

/**
 * @typedef {Object} TenantIdentityProvidersResource_Directory
 * @property {String} name
 * @property {String} idp_tenant_identifier
 * @property {String[]} domains
 * @property {Boolean} groups_enabled
 * @property {Boolean} admin_consent_granted
 * @property {Boolean} universal_print_enabled
 * @property {String} universal_print_user
 * @property {String} primary_domain
 * @property {TenantIdentityProvidersResource_OIDCConfig} configuration
 * @property {Boolean} onedrive_granted
 * @property {Boolean} sharepoint_granted
 * @property {Boolean} global_admin_restriction
 */

/**
 * @typedef {Object} TenantIdentityProvidersResource_Provider
 * @property {String} provider_name
 * @property {Boolean} is_public
 * @property {Object.<string, TenantIdentityProvidersResource_Directory>} directories
 */

/**
 * @typedef {Object} TenantIdentityProvidersResource
 * @property {Object.<string,TenantIdentityProvidersResource_Provider>} providers
 */

(function(){

  'use strict';

  /**
   *
   * @param {DestinationProfileService} DestinationProfileService
   */
  function pxCreateDestinationProfile(
    $timeout, $filter, toastr, $http, DestinationProfileService,
    DestinationsModel, IdpProviderService, RegexModel,
    $window, $location, $rootScope,
    EventServiceEvents, NetworkListService, NetworkListPaging
  ) {
    return {
      restrict: 'EA',
      scope: {
        context: '=context',
        tenant: '=tenant',
        me: '=me',
        meta: '=meta',
        inWorkflowContext: '=inWorkflowContext',
        inDestinationProfile: '=inDestinationProfile',
        behavior: "=behavior",
        callback: '&callback',
      },
      templateUrl: 'app/components/settings/destinationProfiles/createDestinationProfile/createDestinationProfile.template.html',

      /**
       * @param {ScopeType} scope
       */
      link: function(scope) {

        // §5.1: idpBrands map derived from IDP_PROVIDERS registry (formerly the local
        // IDP_BRANDS constant). Same shape consumed by template: idpBrands[type].path
        // (logo include) and .name (display label).
        scope.idpBrands = IdpProviderService.descriptors.reduce(function (map, descriptor) {
          map[descriptor.type] = { path: descriptor.logoTemplate, name: descriptor.caption };
          return map;
        }, {});
        scope.idpConnection = '';
        scope.availableDestinations = new DestinationsModel(NetworkListService);
        scope.availableDirectories = null;
        scope.selectedDestination = null;
        scope.selectedDirectory = null;
        scope.selectedField = null;

        scope.networkListService = NetworkListService;
        scope.networkListPaging = new NetworkListPaging();

        scope.destinationProfile = null;
        scope.error = null;
        scope.model = null;
        scope.serverIsWorkingOnIt = false;
        scope.destinationProfileOutdated = false;

        /** @type {Object.<string,TenantIdentityProvidersResource_Provider>} */
        const identityProviders = scope.tenant.info._embedded.tenantIdentityProviders.providers;
        scope.ILLEGAL_CHARS = new RegexModel().badNameString().string;

        (() => {
          setListeners();
          init();
        })();

        function init () {
          const destinationProfile = scope.inDestinationProfile;
          // If the dialog was opened to edit an existing destination profile, fill in the fields using the profile.
          if (destinationProfile?.info) {
            const destination = scope.availableDestinations[destinationProfile.info.type] ?? null;
            if (destination) {
              scope.selectedDestination = destination;
              scope.destinationProfile = destinationProfile;
              scope.model = destination.createProfileModel(destinationProfile);
              scope.availableDirectories = getDirectoriesOfProvider(identityProviders, destination.identityProviderName);
              if (scope.availableDirectories) {
                const directoryId = destinationProfile.info.directory;
                if (directoryId) {
                  const directory = scope.availableDirectories.find((directory) =>  directory.idp_tenant_identifier === directoryId);
                  if (directory) {
                    selectDirectory(directory);
                  } else {
                    console.warn('Destination profile should have valid "directoryId" property.', destinationProfile, scope.availableDirectories);
                  }
                } else {
                  console.warn('Destination profile should have a "directoryId" property.', destinationProfile);
                }
              }

              if (destinationProfile.info.type === 'SharePoint') {
                // Initialize the paginated select controls when a SharePoint profile is opened for editing.
                setTimeout(() => sharePointFlow());
              }
            } else {
              console.error('Destination profile type "' + destinationProfile.info.type + '" is unknown.');
            }
          }
        }

        /**
         * Gets the list of Identity Provider directories.
         * @param {Object.<string,TenantIdentityProvidersResource_Provider>} identityProviders
         * @param {TenantIdentityProviderName} providerName The name of the Identity Provider whose directories should be returned.
         * @returns {TenantIdentityProvidersResource_Directory[] | null} The array of Identity Provider directories.
         */
        function getDirectoriesOfProvider(identityProviders, providerName) {
          if (!providerName) {
            return null;
          }

          const identityProvider = identityProviders[providerName];
          if (!identityProvider) {
            return [];
          }

          const directories = Object.values(identityProvider.directories)
          directories.forEach((directory) => {
            directory.provider_name = providerName;
          });

          return directories;
        }

        /**
         * @param {TenantIdentityProvidersResource_Directory} directory
         */
        function selectDirectory(directory) {
          scope.selectedDirectory = directory;

          const destination = scope.selectedDestination;
          if (destination.isAccessGranted && destination.isAccessGranted(directory)) {
            // If admin consent is granted for the selected Azure directory, update the UI to reflect the changes accordingly.
            updateDirectory(directory.idp_tenant_identifier);
          }
        }

        scope.selectDirectory = selectDirectory;

        /**
         * @param {String} fieldName The name of the field that lost the input focus.
         */
        scope.onFieldLostFocus = (fieldName) => {
          const field = scope.model.fields[fieldName];
          if (field && field.validate) {
            field.validate(scope.model);
          }
        }

        /**
         * @param {String} fieldName The name of the field whose value has changed.
         */
        scope.onFieldChanged = (fieldName) => {
          const field = scope.model.fields[fieldName];
          if (field) {
            // by default, we assume that the field is valid so, on change the validation error message will disappear
            field.error = null;
          }
        }

        /**
         * Set the destination type and create a new profile for this destination .
         *
         * @param {DestinationType} destination The destination to select.
         */
        scope.selectDestination = (destination) => {
          scope.selectedDestination = destination;
          scope.availableDirectories = getDirectoriesOfProvider(identityProviders, destination.identityProviderName);
          scope.model = null;

          scope.destinationProfile = null;
          scope.serverIsWorkingOnIt = true;
          DestinationProfileService.createProfile(scope.selectedDestination.internalName)
            .then((destinationProfile) => {
              scope.destinationProfile = destinationProfile;
              updateCreateDestinationFlow();
              scope.serverIsWorkingOnIt = false;
              scope.model = scope.selectedDestination.createProfileModel(destinationProfile);

              if (scope.availableDirectories && scope.availableDirectories.includes(scope.selectedDirectory)) {
                // Keep the previously selected Identity Provider directory as it's available for the newly selected destination too.
                selectDirectory(scope.selectedDirectory);
              }
              else if (scope.availableDirectories && scope.availableDirectories.length === 1) {
                // If only one directory is available for the newly selected destination type, select this Identity Provider directory.
                selectDirectory(scope.availableDirectories[0]);
              }
              else {
                scope.selectedDirectory = null;
              }

            }).catch((error) => {
              console.log(error);
              scope.serverIsWorkingOnIt = false;
            });
        }

        /**
         * @param {DestinationProfileModel} updatedDestinationProfile
         */
        function progressDestionationProfileFlow(updatedDestinationProfile) {
          scope.serverIsWorkingOnIt = true;
          DestinationProfileService.updateProfile(scope.destinationProfile, updatedDestinationProfile)
            .then(function(destinationProfile) {
              scope.destinationProfile = destinationProfile
              updateCreateDestinationFlow()
              scope.serverIsWorkingOnIt = false;
            }).catch(function(error) {
              updateCreateDestinationFlow();
              handleDestinationServiceError(error, updatedDestinationProfile);
            });
        }

        scope.clearPopups = function () {
          scope.networkSelectConfiguration = null;
          scope.networkSelectPopupObject = null;
        }

        scope.openNetworkSelectPopup = function(event, field) {
          event.stopPropagation();
          scope.clearPopups();
          scope.selectedField = field;
          scope.networkListPaging.pagingCriteria.pageSize = 5;
          scope.networkListPaging.pagingCriteria.match = null;
          scope.networkSelectConfiguration = {
            paginationObject: scope.networkListPaging,
            placeholderText: 'Search',
            service: 'NetworkListService',
            contentHeight: 463,
          };
          scope.networkSelectPopupObject = {
            dimensions: {height: 463, width: 400},
            offset: {top: 0, left: 0},
            pointerDirection: 'down',
            pointerPosition: 'right',
            relativePosition: 'middle',
            absoluteX: event.clientX,
            absoluteY: event.clientY,
            context: 'selectNetwork',
            type: 'PAGINATION',
            title: 'SelectNetwork',
            confirmText: 'SelectNetwork',
            clickOutsideToClose: false
          }
        };

        scope.clearError = () => {
          scope.error = null;

          if (scope.destinationProfileOutdated) {
            // Reload the page if the destination profile has been modified by someone else.
            // This will checkout the profile again and ensure that the most up-to-date version is loaded.
            $window.location.reload();
          }
        }

        scope.closeNetworkSelectPopup = function(selectedNetwork) {
          scope.selectedField.selectedItem = selectedNetwork;
          scope.selectedField.error = null;
          scope.selectedField = null;
        }

        scope.getPrimaryButtonText = function () {
          return scope.behavior === 'create' ? 'Create' : 'Save';
        };

        scope.isPrimaryButtonDisabled = function () {
          return !scope.selectedDestination || (scope.selectedDestination.isAccessGranted && !scope.selectedDestination.isAccessGranted(scope.selectedDirectory));
        };

        function finalizeDestinationProfile() {

          if (!scope.model || !scope.model.fields) {
            console.error("Failed to create destination profile because model fields do not exist.");
            return;
          }

          // Validate all fields.
          let validationPassed = true;
          Object.values(scope.model.fields).forEach(field => {
            if (field.validate) {
              field.validate(scope.model);
              validationPassed = validationPassed && !field.error;
            }
          });

          if (!validationPassed) {
            console.warn("Destination profile validation failed.");
            return;
          }

          // Copy the actual field values back to the destination profile object and change the state to 'CREATED'.
          const updatedDestinationProfile = angular.copy(scope.destinationProfile)
          if (scope.selectedDirectory) {
            updatedDestinationProfile.info.directory = scope.selectedDirectory.idp_tenant_identifier;
          }

          Object.keys(scope.model.fields).forEach(key => {
            const field = scope.model.fields[key];
            let value = null;
            if (field && (typeof field.value === 'string' || field.value instanceof String)) {
              value = field.value;
            } else {
              return;
            }
            updatedDestinationProfile.info[key] = value
          });

          scope.serverIsWorkingOnIt = true;

          DestinationProfileService.updateProfile(scope.destinationProfile, updatedDestinationProfile).then((destinationProfile) => {
            scope.destinationProfile = destinationProfile;

            DestinationProfileService.commitProfileChanges(destinationProfile)
              .then((destinationProfile) => {
                scope.serverIsWorkingOnIt = false;
                scope.destinationProfile = destinationProfile;
                closeDialog('OK');
                toastr.success($filter('translate')('DestinationProfileSaved'));
              }).catch((error) => handleDestinationServiceError(error, updatedDestinationProfile));

          }).catch((error) => handleDestinationServiceError(error, updatedDestinationProfile));
        }

        scope.finalizeDestinationProfile = finalizeDestinationProfile;

        /**
         * Handle errors returned by the Destination Service.
         *
         * @param {angular.IHttpResponse<PrintixErrorResponse>} error The object describing the error details returned by External API.
         * @param {DestinationProfileModel} destinationProfile The destination profile for which the error was reported.
         */
        function handleDestinationServiceError(error, destinationProfile) {
          console.error("Cannot save destination profile", destinationProfile, error);
          switch (error?.data?.errorCode) {
            case "DESTINATION_PROFILE_CONFLICT":
              scope.error = error;
              scope.destinationProfileOutdated = true;
              break;

            case "DESTINATION_PROFILE_NAME_CONFLICT":
              // Show the error dialog
              const destinationInfo = scope.availableDestinations[destinationProfile.info.type];
              error.data.parameters = [ destinationInfo ? destinationInfo.displayName : 'Unknown' ];
              error.data.manualLink = destinationInfo ? destinationInfo.helpUrl : null;
              scope.error = error;
              break;

            case "FIELD_VALIDATION_ERROR":
              // Show the localized error message under the given input field
              const fieldId = error.data.parameters[0];
              scope.model.fields[fieldId].error = $filter('translate')(error.data.errorCode);
              break;

            default:
              // Reset the errorCode to avoid showing up generic error messages lik "Item does not exist" for "RESOURCE_NOT_FOUND" error code.
              error.data.errorCode = '';
              // Show the error dialog
              scope.error = error;
              break
          }

          scope.serverIsWorkingOnIt = false;
        }


        function updateCreateDestinationFlow () {
          switch (scope.destinationProfile.info.type) {
            case 'SharePoint':
              sharePointFlow()
              break;
          }
        }

        function sharePointFlow () {

          $rootScope.$broadcast('SHAREPOINT_SELECT_SITE_UPDATE', {
            dependsOn: scope.destinationProfile.info.site,
            enabled: true,
            endpoint: scope.destinationProfile.info._links['px:possibleSites'].href,
            selectedItem: !scope.destinationProfile.info.site ? undefined : {
              name: scope.destinationProfile.info.siteName,
              value: scope.destinationProfile.info.site,
            },
          });

          $rootScope.$broadcast('SHAREPOINT_SELECT_DOCUMENT_LIBRARY_UPDATE', {
            dependsOn: scope.destinationProfile.info.site,
            enabled: scope.destinationProfile.info.site,
            endpoint: scope.destinationProfile.info._links['px:possibleDocumentLibraries'].href,
            selectedItem: !scope.destinationProfile.info.documentLibrary ? undefined : {
              name: scope.destinationProfile.info.documentLibraryName,
              value: scope.destinationProfile.info.documentLibrary,
            }
          });

          $rootScope.$broadcast('SHAREPOINT_SELECT_FOLDER_UPDATE', {
            dependsOn: scope.destinationProfile.info.site + scope.destinationProfile.info.documentLibrary + scope.destinationProfile.info.folder,
            enabled: scope.destinationProfile.info.site && scope.destinationProfile.info.documentLibrary,
            endpoint: scope.destinationProfile.info._links['px:possibleFolders'].href,
            selectedItem: !scope.destinationProfile.info.folder ? undefined : {
              name: scope.destinationProfile.info.folderName,
              value: scope.destinationProfile.info.folder,
            }
          });
        }

        /**
         * Handles the directory changed event.
         *
         * @param {String} directoryId The newly selected Azure Directory ID.
         */
        function updateDirectory(directoryId) {
          if (!scope.destinationProfile) {
            // There's no destination profile to update.
          } else if (scope.destinationProfile.info.type === 'SharePoint') {
            const updatedDestinationProfile = angular.copy(scope.destinationProfile);
            updatedDestinationProfile.info.directory = directoryId;
            updatedDestinationProfile.info.site = '';
            updatedDestinationProfile.info.documentLibrary = '';
            updatedDestinationProfile.info.folder = '';
            scope.model.fields.site.error = null;
            scope.model.fields.documentLibrary.error = null;
            $rootScope.$broadcast('SHAREPOINT_SELECT_SITE_RESET_UI_SELECT');
            $rootScope.$broadcast('SHAREPOINT_SELECT_DOCUMENT_LIBRARY_RESET_UI_SELECT');
            $rootScope.$broadcast('SHAREPOINT_SELECT_FOLDER_RESET_UI_SELECT');
            progressDestionationProfileFlow(updatedDestinationProfile);
          } else if (scope.destinationProfile.info.type === 'OneDriveForBusiness') {
            const updatedDestinationProfile = angular.copy(scope.destinationProfile);
            updatedDestinationProfile.info.directory = directoryId;
            progressDestionationProfileFlow(updatedDestinationProfile);
          }
        }

        scope.updateSharePointSite = (item) => {
          const updatedDestinationProfile = angular.copy(scope.destinationProfile);
          updatedDestinationProfile.info.site = item.value;
          updatedDestinationProfile.info.documentLibrary = '';
          updatedDestinationProfile.info.folder = '';
          scope.model.fields.site.error = null;
          scope.model.fields.documentLibrary.error = null;
          $rootScope.$broadcast('SHAREPOINT_SELECT_DOCUMENT_LIBRARY_RESET_UI_SELECT');
          $rootScope.$broadcast('SHAREPOINT_SELECT_FOLDER_RESET_UI_SELECT');
          progressDestionationProfileFlow(updatedDestinationProfile);
        }

        scope.updateSharePointDocLib = (item) => {
          const updatedDestinationProfile = angular.copy(scope.destinationProfile);
          updatedDestinationProfile.info.documentLibrary = item.value;
          updatedDestinationProfile.info.folder = '';
          scope.model.fields.documentLibrary.error = null;
          $rootScope.$broadcast('SHAREPOINT_SELECT_FOLDER_RESET_UI_SELECT');
          progressDestionationProfileFlow(updatedDestinationProfile);
        }

        scope.updateSharePointFolder = (item) => {
          const updatedDestinationProfile = angular.copy(scope.destinationProfile);
          updatedDestinationProfile.info.folder = item.value;
          progressDestionationProfileFlow(updatedDestinationProfile);
        }

        function getDirectory(directoryId) {
          scope.serverIsWorkingOnIt = true;
          $http.get(directoryId).then(function(response) {
            const directory = response.data;
            directory['provider_name'] = scope.selectedDirectory.provider_name;
            directory['id'] = scope.selectedDirectory.id
            selectDirectory(directory)

            scope.serverIsWorkingOnIt = false;
          }).catch(function(error) {
            scope.serverIsWorkingOnIt = false;
          });
        }


        function getIdpDirectories () {
          const idpURL = scope.tenant.info._embedded.tenantIdentityProviders.providers[scope.idpConnection]._links.self.href
          $http.get(idpURL).then(function(response) {
            scope.tenant.info._embedded.tenantIdentityProviders.providers[scope.idpConnection].directories = response.data.directories
            init()
          }).catch(function(error) {});
        }


        function getNewDirectory (directoryId) {
          $http.get(directoryId).then(function(response) {
            scope.tenant.info._embedded.tenantIdentityProviders.providers[scope.idpConnection].directories[response.data.primary_domain] = response.data
            init()
          }).catch(function(error) {});
        }

        /**
         * Closes the dialog with the specified button.
         * @param {'OK' | 'CANCEL'} button The button that resulted the dialog closing.
         */
        function closeDialog(button) {
          if (button === 'OK') {
            scope.callback({destinationProfile: scope.destinationProfile});
          } else if (button === 'CANCEL') {
            scope.callback();
          }
        }

        scope.closeDialog = closeDialog;


        scope.grantAccess = () => {
          const directory = scope.selectedDirectory;
          const domainUrl = $location.absUrl().split('/admin');
          const redirect = scope.selectedDestination.getAdminConsentUrl(directory)
            .replace('{domainUrl}', domainUrl[0]);
          $http.get(redirect).then(function(response) {
            $window.open(response.data.href, '_blank');
          }).catch(function(error) {});
        };

        ////////////////////////////////////////////
        ////////////////////////////////////////////
        // TOOLTIP STUFF
        ////////////////////////////////////////////
        scope.showTooltip = function (showTip, position, type, context) {
          if (showTip) {
            const containerElem = angular.element('.context-content-container-alt')[0];
            let offsetLeft  = containerElem ? containerElem.offsetLeft : 0;
            let offsetTop  = containerElem ? containerElem.offsetTop : 0;
            position.startX = position.startX - offsetLeft;
            position.startY = position.startY - offsetTop;
            tooltip(position, type, context)
          } else {
            scope.clearTooltip();
          }
        }


        function tooltip (position, type, context) {
          scope.clearTooltip();
          scope.tooltipContent = {
            title: type.charAt(0).toUpperCase() + type.slice(1),
            type: type,
            context: context,
          }
          scope.tooltipObject = {
            position: {left: position.startX, top: position.startY + 20},
            contextMenu: true
          }
        }


        scope.clearTooltip = function () {
          scope.tooltipContent = null;
          scope.tooltipObject = null;
        }
        ////////////////////////////////////////////
        // TOOLTIP STUFF
        ////////////////////////////////////////////
        ////////////////////////////////////////////


        scope.connectToAzure = function() {
          scope.idpConnection = 'azure';
          const activationUrl = scope.tenant.info._embedded.tenantIdentityProviders.providers.azure._links['px:tenant_activation_link'].href;
          const domainUrl = $location.absUrl().split('/admin');
          const activationUrlAndRedirect = activationUrl + domainUrl[0]  + '/admin/app/components/authenticationSettings/MSIdentity/accept.html';
          $http.get(activationUrlAndRedirect).then(function(response) {
            $window.open(response.data.authentication_url, '_blank')
          }).catch(function(error) {});
        }


        function setListeners () {
          scope.$on(EventServiceEvents.CHANNELS.ALL_TENANT_ADMINS.EVENTS.IDENTITY_PROVIDER_UPDATED, function(name, data) {
            if (data.update && (data.update === 'SEND_TO_SHAREPOINT_ACCEPTED' || data.update === 'SEND_TO_ONEDRIVE_ACCEPTED')) {
              if (scope.selectedDirectory._links.self.href === data.directory) {
                getDirectory(data.directory)
              }
            }
          });
          scope.$on(EventServiceEvents.CHANNELS.ALL_TENANT_ADMINS.EVENTS.IDENTITY_PROVIDER_CREATED, function(name, data) {
            getNewDirectory(data.directory)
          });
          scope.$on(EventServiceEvents.CHANNELS.ALL_TENANT_ADMINS.EVENTS.IDENTITY_PROVIDER_DELETED, function(name, data) {
            // removeIdentityProviderDirectory(data.directory);
            // getIdpDirectories();
            $window.location.reload();
          });
        }

        scope.$on('$destroy', () => {});
      }
    };
  }

  pxCreateDestinationProfile.$inject = [
    '$timeout', '$filter', 'toastr', '$http', 'DestinationProfileService',
    'DestinationsModel', 'IdpProviderService', 'RegexModel',
    '$window', '$location', '$rootScope',
    'EventServiceEvents', 'NetworkListService', 'NetworkListPaging',
  ];

  angular
    .module('printix-admin')
    .directive('pxCreateDestinationProfile', pxCreateDestinationProfile);

})();
